import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ChevronLeft, ChevronRight, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import ConstructionContextFilter from './ConstructionContextFilter';
import ConstructionBreadcrumb from './ConstructionBreadcrumb';
import { loadProjectNodes } from '../lib/constructionCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const dt = (v) => (v ? String(v).slice(0, 10) : '—');
const STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'ON_HOLD', 'UNKNOWN'];
const STATUS_BADGE = {
  NOT_STARTED: 'bg-slate-100 text-slate-600', IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700', ON_HOLD: 'bg-amber-100 text-amber-700', UNKNOWN: 'bg-slate-100 text-slate-500',
};
const PAGE = 25;

// Server-side searchable/filterable/paginated activity table (built for 10k+ schedules).
const ActivityExplorer = ({ projectId, scheduleId, preset, onOpenActivity }) => {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(preset?.status || '');
  const [wbsId, setWbsId] = useState('');
  const [ctxFilter, setCtxFilter] = useState({});
  const [overdue, setOverdue] = useState(!!preset?.overdue);
  const [dateFrom, setDateFrom] = useState(preset?.dateFrom || '');
  const [dateTo, setDateTo] = useState(preset?.dateTo || '');
  const [boqMapped, setBoqMapped] = useState('');
  const [hasEvidence, setHasEvidence] = useState('');
  const [wbsNodes, setWbsNodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadProjectNodes(projectId).then((d) => setWbsNodes(d.wbs || [])); }, [projectId]);

  const load = useCallback(async () => {
    if (!scheduleId) { setData({ items: [], total: 0 }); setLoading(false); return; }
    setLoading(true);
    try {
      const params = {
        page, pageSize: PAGE, search: search || undefined, status: status || undefined,
        wbsId: wbsId || undefined, overdue: overdue || undefined,
        dateFrom: dateFrom || undefined, dateTo: dateTo || undefined,
        boqMapped: boqMapped === '' ? undefined : boqMapped === 'yes',
        hasEvidence: hasEvidence === '' ? undefined : hasEvidence === 'yes',
        ...ctxFilter,
      };
      const d = await axios.get(`${API}/projects/${projectId}/schedules/${scheduleId}/activities`, { params }).then((r) => r.data);
      setData(d);
    } catch (e) { toast.error('Failed to load activities'); } finally { setLoading(false); }
  }, [projectId, scheduleId, page, search, status, wbsId, ctxFilter, overdue, dateFrom, dateTo, boqMapped, hasEvidence]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE));
  const rst = (fn) => (v) => { fn(v); setPage(1); };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => rst(setSearch)(e.target.value)} placeholder="Search activity name / code" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="activity-search" />
        </div>
        <select value={status} onChange={(e) => rst(setStatus)(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="activity-status-filter">
          <option value="">Status: All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        {wbsNodes.length > 0 && (
          <select value={wbsId} onChange={(e) => rst(setWbsId)(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="activity-wbs-filter">
            <option value="">WBS: All</option>
            {wbsNodes.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        )}
        <select value={boqMapped} onChange={(e) => rst(setBoqMapped)(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="activity-boq-filter">
          <option value="">BOQ: Any</option><option value="yes">BOQ mapped</option><option value="no">Not BOQ mapped</option>
        </select>
        <select value={hasEvidence} onChange={(e) => rst(setHasEvidence)(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="activity-evidence-filter">
          <option value="">Evidence: Any</option><option value="yes">Has evidence</option><option value="no">No evidence</option>
        </select>
      </div>

      <ConstructionContextFilter projectId={projectId} value={ctxFilter} onChange={rst(setCtxFilter)} testId="activity-context-filter" />

      <div className="flex items-center gap-3 flex-wrap text-sm">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="activity-overdue-label">
          <input type="checkbox" checked={overdue} onChange={(e) => rst(setOverdue)(e.target.checked)} className="w-4 h-4 accent-red-600" data-testid="activity-overdue-toggle" />
          <span className="flex items-center gap-1 text-slate-600"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /> Overdue only</span>
        </label>
        <span className="text-slate-400">|</span>
        <label className="flex items-center gap-1 text-slate-500 text-xs">From <input type="date" value={dateFrom} onChange={(e) => rst(setDateFrom)(e.target.value ? `${e.target.value}T00:00:00Z` : '')} className="px-2 py-1 border border-slate-300 rounded text-xs" data-testid="activity-date-from" /></label>
        <label className="flex items-center gap-1 text-slate-500 text-xs">To <input type="date" value={dateTo ? dateTo.slice(0, 10) : ''} onChange={(e) => rst(setDateTo)(e.target.value ? `${e.target.value}T23:59:59Z` : '')} className="px-2 py-1 border border-slate-300 rounded text-xs" data-testid="activity-date-to" /></label>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="activity-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-4 py-2">Code</th><th className="px-4 py-2">Activity</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Planned Start</th><th className="px-3 py-2">Planned Finish</th><th className="px-4 py-2">Location</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400" data-testid="activity-empty">No activities match.</td></tr>
            ) : data.items.map((a) => (
              <tr key={a.id} onClick={() => onOpenActivity(a.id)} className="border-b border-slate-50 hover:bg-blue-50 cursor-pointer" data-testid="activity-row">
                <td className="px-4 py-3 font-medium text-slate-800">{fmt(a.activityCode)}</td>
                <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{a.activityName}</td>
                <td className="px-3 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_BADGE[a.status] || STATUS_BADGE.UNKNOWN}`}>{a.status}</span></td>
                <td className="px-3 py-3 text-slate-600">{dt(a.plannedStart)}</td>
                <td className="px-3 py-3 text-slate-600">{dt(a.plannedFinish)}</td>
                <td className="px-4 py-3"><ConstructionBreadcrumb projectId={projectId} context={a.constructionContext} fallback="Unmapped" testId={`act-ctx-${a.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{data.total} activities</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1 disabled:opacity-30" data-testid="activity-prev-page"><ChevronLeft className="w-4 h-4" /></button>
          <span>Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="p-1 disabled:opacity-30" data-testid="activity-next-page"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default ActivityExplorer;
